import type { paths } from "../types/address-book-api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIContact =
  paths["/api/v1/contacts"]["get"]["responses"]["200"]["content"]["application/json"]["contacts"][number];

export type Contact = APIContact;

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type ContactsListResponse =
  paths["/api/v1/contacts"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Filter Types
// ============================================================================

type ContactsListFilters = {
  /** Address-book company UUID. Maps to the `companyId` query param. */
  companyId?: string;
  companyWpId?: number;
  officeWpId?: number;
  search?: string;
  /** Address Book `offices.country_id` (WP country id). Maps to `office.countryId`. */
  countryId?: number;
  /** Only match the company/office/country filters against active (non-ended) roles. */
  activeRolesOnly?: boolean;
  /** Restrict to these Address Book contact uuids. */
  ids?: string[];
  limit?: number;
  offset?: number;
};

type GetByNameResult = {
  contactId: number;
  name: string;
  companyId: number | null;
  companyName: string | null;
};

type GetByNameResponse = {
  contacts: GetByNameResult[];
  requestId: string;
};

type SingleContactResponse =
  paths["/api/v1/contacts/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

export const contacts = {
  getAll: async (filters?: ContactsListFilters): Promise<ContactsListResponse> => {
    const params = new URLSearchParams();
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.companyWpId) params.append("companyWpId", filters.companyWpId.toString());
    if (filters?.officeWpId) params.append("officeWpId", filters.officeWpId.toString());
    if (filters?.search) params.append("name", filters.search);
    if (filters?.countryId !== undefined) {
      params.append("office.countryId", filters.countryId.toString());
    }
    if (filters?.activeRolesOnly !== undefined) {
      params.append("activeRolesOnly", filters.activeRolesOnly ? "true" : "false");
    }
    if (filters?.ids !== undefined) params.append("ids", filters.ids.join(","));
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<ContactsListResponse>(`/v1/address-book/contacts${query}`);
  },
  /**
   * Fetch a single contact by its address-book UUID. Returns null on 404 so
   * callers (e.g. the contact picker) can render a fallback for a stale id.
   */
  getById: async (id: string): Promise<Contact | null> => {
    if (!id) return null;
    try {
      const resp = await api.get<SingleContactResponse>(
        `/v1/address-book/contacts/${encodeURIComponent(id)}`,
      );
      return resp.contact;
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return null;
      throw err;
    }
  },
  getByName: async (params: {
    name: string;
    firmRef?: string;
    companyId?: number;
  }): Promise<GetByNameResponse> => {
    const qs = new URLSearchParams();
    qs.append("strictMode", "false");
    qs.append("name", params.name);
    if (params.firmRef) qs.append("firmRef", params.firmRef);
    return api.get<GetByNameResponse>(`/v1/address-book/contacts/by-name?${qs.toString()}`);
  },
};
